import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { pushNotificationService, PushNotificationData } from '@/services/pushNotificationService';
import { logger } from '@/utils/logger';

interface UsePushNotificationsOptions {
  userId: string | null;
  userType: 'buyer' | 'seller';
  enabled?: boolean;
}

export function usePushNotifications({ userId, userType, enabled = true }: UsePushNotificationsOptions) {
  const navigate = useNavigate();
  const location = useLocation();

  // Handle navigation when a notification is tapped
  const handleNotificationNavigation = useCallback((data: PushNotificationData) => {
    logger.info('[usePushNotifications] Handling notification navigation:', data);

    const type = data.type;
    const propertyId = data.propertyId;
    const offerId = data.offerId;

    // Map notification type to route
    switch (type) {
      case 'new_property':
      case 'followed_seller_listing':
        if (propertyId) {
          navigate(`/buyer/properties/${propertyId}`);
        } else {
          navigate('/buyer/search');
        }
        break;

      case 'offer_accepted':
      case 'offer_rejected':
      case 'offer_auto_rejected':
      case 'offer_cancelled':
        navigate('/buyer/offers');
        break;

      case 'property_offer':
      case 'offer_withdrawn':
        navigate('/seller/offers');
        break;

      case 'task_reminder':
        navigate('/seller/deal-tracker');
        break;

      default:
        // Default navigation based on user type
        if (userType === 'buyer') {
          navigate('/buyer/notifications');
        } else {
          navigate('/seller/notifications');
        }
        break;
    }
  }, [navigate, userType]);

  // Initialize push notifications
  useEffect(() => {
    if (!userId || !enabled) {
      return;
    }

    // Only initialize on native platforms
    if (!pushNotificationService.isAvailable()) {
      return;
    }

    const initializePush = async () => {
      try {
        // Set up navigation handler before initializing
        pushNotificationService.setNavigationCallback(handleNotificationNavigation);

        // Initialize push notifications
        const success = await pushNotificationService.initialize();
        if (!success) {
          logger.warn('[usePushNotifications] Failed to initialize push notifications');
          return;
        }

        // Register token with backend
        await pushNotificationService.registerToken(userId, userType);
        logger.info('[usePushNotifications] Push notifications initialized for', userType);
      } catch (error) {
        logger.error('[usePushNotifications] Error initializing:', error);
      }
    };

    initializePush();
  }, [userId, userType, enabled, handleNotificationNavigation]);

  // Clear token on unmount (logout)
  const clearPushToken = useCallback(async () => {
    if (userId && pushNotificationService.isAvailable()) {
      await pushNotificationService.clearToken(userId, userType);
    }
  }, [userId, userType]);

  // Update push enabled status
  const updatePushEnabled = useCallback(async (isEnabled: boolean) => {
    if (!userId) return false;
    return pushNotificationService.updatePushEnabled(userId, userType, isEnabled);
  }, [userId, userType]);

  // Check if push is available
  const isPushAvailable = pushNotificationService.isAvailable();

  return {
    isPushAvailable,
    clearPushToken,
    updatePushEnabled
  };
}
